import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Search, ChevronDown } from 'lucide-react';
import { NAV, COMPANY } from '../../data/content';
import SearchDialog from './SearchDialog';
import SocialIcons from './SocialIcons';

function Logo({ onClick }) {
  return (
    <Link
      to="/"
      onClick={onClick}
      data-testid="logo-link"
      className="flex items-center gap-3"
    >
      <span className="flex h-10 w-10 items-center justify-center bg-navy font-display text-lg font-bold text-white">
        B
      </span>
      <span className="leading-none">
        <span className="block font-display text-lg font-bold tracking-tight text-navy">
          BANMORE
        </span>
        <span className="mono-label block text-[0.6rem] text-steel">
          Electricals · Since 1977
        </span>
      </span>
    </Link>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-40 bg-white transition-[box-shadow,border-color] duration-500 ${
          scrolled ? 'border-b border-line shadow-sm' : 'border-b border-line'
        }`}
      >
        <div className="container-x flex h-20 items-center justify-between">
          <Logo />

          <nav className="hidden items-center gap-1 lg:flex" data-testid="desktop-nav">
            {NAV.map((item) => (
              <div key={item.label} className="group relative">
                <Link
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-navy/80 transition-colors duration-300 hover:text-brand"
                >
                  {item.label}
                  {item.children && <ChevronDown className="h-3.5 w-3.5" />}
                </Link>
                {item.children && (
                  <div className="invisible absolute left-0 top-full w-72 translate-y-2 border border-line bg-white p-2 opacity-0 shadow-xl transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                    {item.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        data-testid={`nav-child-${c.to}`}
                        className="block px-3 py-2.5 text-sm text-navy/75 transition-colors duration-200 hover:bg-mist hover:text-brand"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <SocialIcons className="mr-1 hidden md:flex" />
            <button
              data-testid="search-open-btn"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className="flex h-10 w-10 items-center justify-center text-navy/70 transition-colors hover:text-brand"
            >
              <Search className="h-5 w-5" />
            </button>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className="flex h-10 w-10 items-center justify-center text-navy lg:hidden"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 bg-navy text-white lg:hidden"
            data-testid="mobile-menu"
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="container-x flex h-20 items-center justify-between">
              <span className="font-display text-lg font-bold">BANMORE</span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="flex h-10 w-10 items-center justify-center"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="container-x mt-6 flex flex-col gap-1 overflow-y-auto pb-16">
              {NAV.map((item) => (
                <div key={item.label} className="border-b border-white/10 py-2">
                  <Link
                    to={item.to}
                    className="block py-2 font-display text-2xl font-medium"
                    data-testid={`mnav-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <div className="mb-2 flex flex-col gap-1 pl-1">
                      {item.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          className="py-1.5 text-sm text-white/60"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <a
                href={`tel:${COMPANY.phone}`}
                className="mt-6 mono-label text-brand-light"
              >
                {COMPANY.phone}
              </a>
              <SocialIcons tone="dark" className="mt-4 gap-2" />
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}
